import { useState, useCallback, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://d14e64x71m.execute-api.us-east-1.amazonaws.com/prod";

// ── CSV export helper ─────────────────────────────────────────────────────
function downloadCSV(results, userInfo, employeeId) {
  const header = `Exported Access Search Results For EmployeeID: ${employeeId} | Name: ${userInfo?.displayName || ""} | Email: ${userInfo?.email || ""}`;
  const cols   = ["Source", "Access Profile", "Grant Type", "Description"];
  const escape = (val) => `"${String(val || "").replace(/"/g, '""')}"`;
  const rows   = results.map(r =>
    [r.sourceName, r.displayName, r.revocable, r.description].map(escape).join(",")
  );
  const csv  = [header, cols.map(escape).join(","), ...rows].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `access-export-${employeeId}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Inline employee lookup panel ──────────────────────────────────────────
function EmployeeLookupPanel({ onSelect }) {
  const [firstName, setFirstName] = useState("");
  const [lastName,  setLastName]  = useState("");
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState("");
  const [results,   setResults]   = useState(null);
  const [selected,  setSelected]  = useState(null);

  const handleSearch = async () => {
    if (!firstName.trim() && !lastName.trim()) { setError("Enter at least a first or last name."); return; }
    setLoading(true); setError(""); setResults(null); setSelected(null);
    try {
      const res  = await fetch(`${API_BASE}/employee-search`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ firstName: firstName.trim(), lastName: lastName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
      setResults(data.results || []);
    } catch (err) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };
  const handleSelect  = (row) => { setSelected(row.employeeNumber); onSelect(row.employeeNumber); };

  return (
    <div className="as-lookup-panel">
      <div className="as-lookup-title">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
        </svg>
        Search by name — click a row to populate the Employee ID field
      </div>
      <div className="as-lookup-inputs">
        <div className="as-lookup-field">
          <label className="control-label">First Name</label>
          <input autoFocus className="cug-input" placeholder="e.g. Dhairya" value={firstName}
            onChange={e => { setFirstName(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown} disabled={loading} />
        </div>
        <div className="as-lookup-field">
          <label className="control-label">Last Name</label>
          <input className="cug-input" placeholder="e.g. Shah" value={lastName}
            onChange={e => { setLastName(e.target.value); setError(""); }}
            onKeyDown={handleKeyDown} disabled={loading} />
        </div>
        <button className="compare-btn" onClick={handleSearch}
          disabled={loading || (!firstName.trim() && !lastName.trim())}
          style={{ alignSelf: "flex-end" }}>
          {loading
            ? <><span className="spinner" /> Searching…</>
            : <>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                Search
              </>}
        </button>
      </div>

      {error && (
        <div className="as-error-bar" style={{ margin: "10px 0 0" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {results !== null && (
        results.length === 0
          ? <div className="as-lookup-empty">No employees found. Try different names.</div>
          : (
            <div className="as-lookup-table-wrap">
              <div className="as-lookup-thead">
                <div className="as-lth">Employee ID</div>
                <div className="as-lth">First Name</div>
                <div className="as-lth">Last Name</div>
                <div className="as-lth">Email</div>
              </div>
              <div className="as-lookup-tbody">
                {results.map((r, i) => (
                  <div key={i}
                    className={`as-lookup-tr ${selected === r.employeeNumber ? "as-lookup-tr-selected" : ""}`}
                    onClick={() => handleSelect(r)}>
                    <div className="as-ltd"><span className="as-emp-id-badge">{r.employeeNumber || "—"}</span></div>
                    <div className="as-ltd">{r.firstName || "—"}</div>
                    <div className="as-ltd">{r.lastName  || "—"}</div>
                    <div className="as-ltd as-ltd-muted">{r.email || "—"}</div>
                  </div>
                ))}
              </div>
            </div>
          )
      )}
    </div>
  );
}

// ── Source filter pills ───────────────────────────────────────────────────
function SourceFilter({ sources, selected, onToggle, onClear }) {
  return (
    <div className="as-source-filter">
      <span className="as-filter-label">Source:</span>
      <div className="as-filter-pills">
        {sources.map(s => (
          <button key={s} className={`as-pill ${selected.includes(s) ? "as-pill-active" : ""}`} onClick={() => onToggle(s)}>
            {s}
          </button>
        ))}
      </div>
      {selected.length > 0 && <button className="as-clear-btn" onClick={onClear}>Clear</button>}
    </div>
  );
}

// ── Results table ─────────────────────────────────────────────────────────
function ResultsTable({ results, searchText, sourceFilter }) {
  const filtered = results.filter(r => {
    const matchSource = sourceFilter.length === 0 || sourceFilter.includes(r.sourceName);
    const q           = searchText.toLowerCase();
    const matchSearch = !q
      || r.displayName.toLowerCase().includes(q)
      || r.sourceName.toLowerCase().includes(q)
      || r.description.toLowerCase().includes(q)
      || r.revocable.toLowerCase().includes(q);
    return matchSource && matchSearch;
  });

  const sorted = [...filtered].sort((a, b) => a.sourceName.localeCompare(b.sourceName));

  if (sorted.length === 0) {
    return (
      <div className="as-empty">
        {results.length === 0
          ? "No access profiles found for this employee."
          : "No results match your current filters."}
      </div>
    );
  }

  return (
    <div className="as-table-wrap">
      <div className="as-table">
        <div className="as-thead">
          <div className="as-th as-col-source">Source</div>
          <div className="as-th as-col-profile">Access Profile</div>
          <div className="as-th as-col-grant">Grant Type</div>
          <div className="as-th as-col-desc">Description</div>
        </div>
        <div className="as-tbody">
          {sorted.map((r, i) => (
            <div key={i} className="as-tr">
              <div className="as-td as-col-source">
                <span className="as-source-badge">{r.sourceName}</span>
              </div>
              <div className="as-td as-col-profile">{r.displayName}</div>
              <div className="as-td as-col-grant">
                <span className={`as-grant-badge as-grant-${r.revocable === "Requested" ? "requested" : "birthright"}`}>
                  {r.revocable}
                </span>
              </div>
              <div className="as-td as-col-desc as-desc-text">{r.description || "—"}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="as-table-footer">
        Showing {sorted.length} of {results.length} access profiles
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────
export default function AccessSearch() {
  const [employeeId,   setEmployeeId]   = useState("");
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState("");
  const [userInfo,     setUserInfo]     = useState(null);
  const [results,      setResults]      = useState(null);
  const [searchText,   setSearchText]   = useState("");
  const [sourceFilter, setSourceFilter] = useState([]);
  const [showLookup,   setShowLookup]   = useState(false);
  const inputRef = useRef(null);

  const sources = results
    ? [...new Set(results.map(r => r.sourceName).filter(Boolean))].sort()
    : [];

  const toggleSource = useCallback((s) =>
    setSourceFilter(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]), []);

  const handleSearch = useCallback(async () => {
    const id = employeeId.trim();
    if (!id) { setError("Please enter an Employee ID."); return; }
    setLoading(true); setError(""); setResults(null); setUserInfo(null);
    setSearchText(""); setSourceFilter([]); setShowLookup(false);
    try {
      const res  = await fetch(`${API_BASE}/access-search`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ employeeId: id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || `API error ${res.status}`);
      if (!data.found) { setError("No user found for that Employee ID. Please check and try again."); return; }
      setUserInfo(data.userInfo);
      setResults(data.results);
    } catch (err) { setError(err.message || "An unexpected error occurred."); }
    finally { setLoading(false); }
  }, [employeeId]);

  const handleKeyDown = (e) => { if (e.key === "Enter") handleSearch(); };

  const handleReset = () => {
    setEmployeeId(""); setResults(null); setUserInfo(null);
    setError(""); setSearchText(""); setSourceFilter([]); setShowLookup(false);
    inputRef.current?.focus();
  };

  const handleExport = () => {
    if (!results) return;
    const visible = results.filter(r =>
      (sourceFilter.length === 0 || sourceFilter.includes(r.sourceName)) &&
      (!searchText || [r.displayName, r.sourceName, r.description, r.revocable]
        .some(v => v?.toLowerCase().includes(searchText.toLowerCase())))
    );
    downloadCSV(visible, userInfo, employeeId);
  };

  const handleLookupSelect = (empId) => {
    setEmployeeId(empId); setShowLookup(false); setError("");
    inputRef.current?.focus();
  };

  return (
    <div className="as-root">

      {/* ── Header ── */}
      <div className="as-header">
        <div className="as-header-inner">
          <div className="logo-mark">V</div>
          <div>
            <div className="as-title">Access Search</div>
            <div className="as-subtitle">Search employee access profiles across myHR applications</div>
          </div>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div className="as-search-bar">
        <div className="as-search-inner">
          <div className="as-search-group">
            <label className="control-label">Employee ID</label>
            <div className="as-search-row">
              <input
                ref={inputRef}
                className="cug-input as-id-input"
                placeholder="Enter Employee ID…"
                value={employeeId}
                onChange={e => { setEmployeeId(e.target.value); setError(""); }}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />
              <button className="compare-btn" onClick={handleSearch} disabled={loading || !employeeId.trim()}>
                {loading
                  ? <><span className="spinner" /> Searching…</>
                  : <>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                      </svg>
                      Search
                    </>}
              </button>
              <button className="cug-reset-btn" onClick={handleReset}>Reset</button>
              <button
                className={`as-lookup-toggle ${showLookup ? "as-lookup-toggle-active" : ""}`}
                onClick={() => setShowLookup(v => !v)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                </svg>
                {showLookup ? "Hide Name Search" : "Don't know the Employee ID?"}
                <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                  style={{ transform: showLookup ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
                  <polyline points="6 9 12 15 18 9"/>
                </svg>
              </button>
            </div>
          </div>
        </div>

        {showLookup && (
          <div className="as-lookup-wrap">
            <EmployeeLookupPanel onSelect={handleLookupSelect} />
          </div>
        )}
      </div>

      {/* ── Error ── */}
      {error && (
        <div className="as-error-bar">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
          </svg>
          {error}
        </div>
      )}

      {/* ── Info panel ── */}
      {results === null && !loading && !error && (
        <div className="as-info-panel">
          <div className="as-info-title">What this tool does</div>
          <div className="as-info-body">
            <p>Enter an Employee ID to view all access profiles assigned to that employee across supported applications.</p>
            <div className="as-info-apps">
              <div className="as-info-apps-label">Supported Applications</div>
              <div className="as-info-app-list">
                {[
                  "myHR Talent Solutions 2.0 – Avature (Production)",
                  "myHR InforIFS PROD",
                  "myHR Tableau PROD",
                  "myHR SAP P41 BE",
                  "myHR InforLandmark PROD",
                  "myHR ServiceNow PROD",
                  "myHR Visier PROD",
                  "QuickSight",
                ].map(app => (
                  <div key={app} className="as-info-app">
                    <span className="as-info-dot" />{app}
                  </div>
                ))}
              </div>
            </div>
            <div className="as-info-features">
              {[
                ["🔍", "Search across all columns instantly"],
                ["🏷",  "Filter by source application"],
                ["📋", "See Grant Type: Requested vs Birthright"],
                ["👤", "Don't know the ID? Search by name inline"],
                ["⬇",  "Export visible results to CSV"],
              ].map(([icon, text]) => (
                <div key={text} className="as-info-feature"><span>{icon}</span> {text}</div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Results ── */}
      {results !== null && (
        <div className="as-results">
          {userInfo && (
            <div className="as-user-bar">
              <div className="as-user-avatar">
                {(userInfo.displayName || "?").split(",")[0]?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="as-user-info">
                <div className="as-user-name">{userInfo.displayName || "Unknown"}</div>
                <div className="as-user-email">{userInfo.email || "—"}</div>
              </div>
              <div className="as-user-stats">
                <span className="as-stat-pill">{results.length} access profile{results.length !== 1 ? "s" : ""}</span>
                <span className="as-stat-pill">{sources.length} source{sources.length !== 1 ? "s" : ""}</span>
              </div>
            </div>
          )}

          <div className="as-controls">
            <input className="search-input" placeholder="Search across all columns…"
              value={searchText} onChange={e => setSearchText(e.target.value)} />
            <button className="compare-btn as-export-btn" onClick={handleExport} disabled={results.length === 0}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="7 10 12 15 17 10"/>
                <line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Export CSV
            </button>
          </div>

          {sources.length > 1 && (
            <SourceFilter sources={sources} selected={sourceFilter} onToggle={toggleSource} onClear={() => setSourceFilter([])} />
          )}

          <ResultsTable results={results} searchText={searchText} sourceFilter={sourceFilter} />
        </div>
      )}

      <style>{`
        .as-root { min-height: 100%; background: linear-gradient(180deg, rgba(112, 204, 152, 0.04) 0, transparent 120px), var(--bg-base); }

        .as-header { background: rgba(35, 55, 71, 0.88); backdrop-filter: blur(10px); border-bottom: 1px solid var(--border); padding: 22px 32px; position: sticky; top: 0; z-index: 10; }
        .as-header-inner { display: flex; align-items: center; gap: 14px; }
        .as-title { font-family: var(--font-display); font-size: 18px; font-weight: 700; color: var(--text-primary); }
        .as-subtitle { font-size: 11px; color: var(--text-muted); margin-top: 2px; }

        .as-search-bar { background: rgba(35, 55, 71, 0.72); border-bottom: 1px solid var(--border); padding: 20px 32px; }
        .as-search-inner { display: flex; align-items: flex-end; gap: 16px; }
        .as-search-group { display: flex; flex-direction: column; gap: 6px; }
        .as-search-row { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .as-id-input { width: 240px !important; }

        .as-lookup-toggle { display: flex; align-items: center; gap: 6px; background: none; border: 1px solid var(--border-light); border-radius: var(--radius-md); color: var(--text-secondary); font-size: 12px; font-family: var(--font-body); padding: 7px 12px; cursor: pointer; transition: color 0.15s, border-color 0.15s, background 0.15s; white-space: nowrap; }
        .as-lookup-toggle:hover { color: var(--green); border-color: var(--green); }
        .as-lookup-toggle-active { color: var(--green) !important; border-color: var(--green) !important; background: var(--green-dim) !important; }

        .as-lookup-wrap { margin-top: 16px; border-top: 1px solid var(--border); padding-top: 16px; }
        .as-lookup-panel { display: flex; flex-direction: column; gap: 14px; }
        .as-lookup-title { display: flex; align-items: center; gap: 6px; font-size: 12px; color: var(--text-muted); font-style: italic; }
        .as-lookup-inputs { display: flex; align-items: flex-end; gap: 10px; flex-wrap: wrap; }
        .as-lookup-field { display: flex; flex-direction: column; gap: 6px; }
        .as-lookup-empty { font-size: 13px; color: var(--text-muted); padding: 12px 0; }

        .as-lookup-table-wrap { border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; max-height: 260px; overflow-y: auto; }
        .as-lookup-thead { display: grid; grid-template-columns: 140px 160px 160px 1fr; background: var(--bg-raised); border-bottom: 1px solid var(--border); position: sticky; top: 0; }
        .as-lth { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); padding: 8px 14px; }
        .as-lth:not(:last-child) { border-right: 1px solid var(--border); }
        .as-lookup-tr { display: grid; grid-template-columns: 140px 160px 160px 1fr; border-bottom: 1px solid var(--border); cursor: pointer; transition: background 0.1s; }
        .as-lookup-tr:last-child { border-bottom: none; }
        .as-lookup-tr:hover { background: var(--bg-hover); }
        .as-lookup-tr-selected { background: var(--green-dim) !important; }
        .as-ltd { padding: 10px 14px; font-size: 13px; color: var(--text-secondary); display: flex; align-items: center; }
        .as-ltd:not(:last-child) { border-right: 1px solid var(--border); }
        .as-ltd-muted { font-size: 12px; color: var(--text-muted); }
        .as-emp-id-badge { font-size: 11px; padding: 2px 8px; border-radius: var(--radius-sm); background: var(--green-dim); color: var(--green); border: 1px solid #1a4a28; font-weight: 600; }

        .as-error-bar { display: flex; align-items: center; gap: 8px; margin: 20px 32px 0; background: var(--red-dim); border: 1px solid #5a1a1a; color: var(--red); border-radius: var(--radius-md); padding: 10px 14px; font-size: 13px; }

        .as-info-panel { margin: 32px 32px 0; background: rgba(35, 55, 71, 0.72); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 26px; max-width: 700px; box-shadow: 0 16px 34px rgba(0,0,0,0.16); }
        .as-info-title { font-family: var(--font-display); font-size: 16px; font-weight: 700; color: var(--text-primary); margin-bottom: 14px; }
        .as-info-body { display: flex; flex-direction: column; gap: 18px; }
        .as-info-body p { font-size: 13px; color: var(--text-secondary); line-height: 1.7; }
        .as-info-apps-label { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); margin-bottom: 10px; }
        .as-info-app-list { display: flex; flex-direction: column; gap: 6px; }
        .as-info-app { display: flex; align-items: center; gap: 8px; font-size: 13px; color: var(--text-secondary); }
        .as-info-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--green); flex-shrink: 0; }
        .as-info-features { display: flex; flex-direction: column; gap: 8px; }
        .as-info-feature { font-size: 13px; color: var(--text-secondary); display: flex; align-items: center; gap: 8px; }

        .as-results { padding: 28px 32px 64px; display: flex; flex-direction: column; gap: 18px; }

        .as-user-bar { display: flex; align-items: center; gap: 14px; background: rgba(35, 55, 71, 0.72); border: 1px solid var(--border); border-radius: var(--radius-lg); padding: 18px 20px; box-shadow: 0 16px 34px rgba(0,0,0,0.14); }
        .as-user-avatar { width: 44px; height: 44px; border-radius: 50%; background: rgba(43, 87, 116, 0.25); border: 2px solid rgba(112, 204, 152, 0.42); color: var(--green-bright); font-size: 18px; font-weight: 700; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .as-user-info { flex: 1; }
        .as-user-name { font-size: 15px; font-weight: 600; color: var(--text-primary); }
        .as-user-email { font-size: 12px; color: var(--text-muted); margin-top: 2px; }
        .as-user-stats { display: flex; gap: 8px; }
        .as-stat-pill { font-size: 11px; padding: 3px 10px; border-radius: 999px; background: rgba(255, 255, 255, 0.03); border: 1px solid var(--border-light); color: var(--text-secondary); }

        .as-controls { display: flex; align-items: center; gap: 10px; }
        .as-export-btn { flex-shrink: 0; }

        .as-source-filter { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
        .as-filter-label { font-size: 11px; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.06em; white-space: nowrap; }
        .as-filter-pills { display: flex; gap: 6px; flex-wrap: wrap; }
        .as-pill { font-size: 11px; padding: 4px 12px; border-radius: 999px; border: 1px solid var(--border-light); background: rgba(255, 255, 255, 0.03); color: var(--text-secondary); cursor: pointer; transition: all 0.15s; }
        .as-pill:hover { border-color: var(--green); color: var(--green); }
        .as-pill-active { background: var(--green-dim) !important; border-color: var(--green) !important; color: var(--green) !important; }
        .as-clear-btn { font-size: 11px; padding: 4px 10px; border-radius: 999px; border: 1px solid var(--border); background: transparent; color: var(--text-muted); cursor: pointer; transition: color 0.15s; }
        .as-clear-btn:hover { color: var(--red); border-color: var(--red); }

        .as-table-wrap { background: rgba(35, 55, 71, 0.72); border: 1px solid var(--border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: 0 16px 34px rgba(0,0,0,0.14); }
        .as-thead { display: grid; grid-template-columns: 220px 260px 120px 1fr; background: rgba(255, 255, 255, 0.03); border-bottom: 1px solid var(--border); }
        .as-th { font-size: 10px; text-transform: uppercase; letter-spacing: 0.07em; color: var(--text-muted); padding: 10px 16px; }
        .as-th:not(:last-child) { border-right: 1px solid var(--border); }
        .as-tr { display: grid; grid-template-columns: 220px 260px 120px 1fr; border-bottom: 1px solid var(--border); transition: background 0.1s; }
        .as-tr:last-child { border-bottom: none; }
        .as-tr:hover { background: var(--bg-hover); }
        .as-td { padding: 11px 16px; font-size: 13px; color: var(--text-secondary); display: flex; align-items: center; }
        .as-td:not(:last-child) { border-right: 1px solid var(--border); }
        .as-desc-text { font-size: 12px; color: var(--text-muted); word-break: break-word; align-items: flex-start; padding-top: 13px; }
        .as-source-badge { font-size: 11px; padding: 2px 8px; border-radius: var(--radius-sm); background: rgba(0, 155, 221, 0.12); color: var(--sp-green-pale); border: 1px solid rgba(0, 155, 221, 0.3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 190px; display: inline-block; }
        .as-grant-badge { font-size: 10px; padding: 3px 9px; border-radius: 999px; font-weight: 500; white-space: nowrap; }
        .as-grant-requested  { background: rgba(112, 204, 152, 0.12); color: var(--green-bright); border: 1px solid rgba(112, 204, 152, 0.28); }
        .as-grant-birthright { background: rgba(184, 220, 237, 0.12); color: var(--amber-bright); border: 1px solid rgba(184, 220, 237, 0.24); }
        .as-table-footer { padding: 10px 16px; font-size: 11px; color: var(--text-muted); border-top: 1px solid var(--border); background: rgba(255, 255, 255, 0.03); }
        .as-empty { padding: 48px 24px; text-align: center; font-size: 13px; color: var(--text-muted); }
      `}</style>
    </div>
  );
}
